// Parentum — Parenting Plan PDF Generator
// Extracted from /app/frontend/src/pages/PlanBuilder.jsx to keep the component
// focused on UI. The function is a pure side-effectful PDF generator that
// downloads the file via `pdf.save(...)` and invokes the optional `onSaved`
// callback after the file is written (the page uses this to persist & grade
// the plan).
import jsPDF from 'jspdf';
import { addPdfHeader, addPdfFooterToAllPages, ensureImagesLoaded } from './pdfBranding';

export async function generateParentingPlanPdf(planData, onSaved) {
  await ensureImagesLoaded();
  const pdf = new jsPDF();
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentW = pw - margin * 2;
  const footerZone = 22;
  let y = 0;

  // ===== HELPERS =====
  // Title-case proper names (preserves mixed-case like "McDonald", "O'Brien";
  // normalizes ALL-CAPS input like "JOHN SMITH" to "John Smith")
  function titleCaseName(n) {
    if (!n || typeof n !== 'string') return n;
    return n.trim().split(/\s+/).map(function (w) {
      if (!w) return '';
      // If the word is ALL-CAPS (no lowercase letters), normalize to Title Case
      if (/^[A-Z]+$/.test(w)) {
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      }
      // Preserve McDonald, O'Brien, etc. (has uppercase past position 0)
      if (/[A-Z]/.test(w.slice(1))) return w;
      // Handle hyphenated names (Smith-Jones)
      return w.split('-').map(function (p) {
        if (!p) return '';
        return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      }).join('-');
    }).join(' ').trim();
  }

  // Grammar, clarity & capitalization enforcement for legal clause text.
  // - Collapses whitespace
  // - Capitalizes defined terms (Plaintiff, Defendant)
  // - Removes duplicate adjacent words (incl. "ORDERED, ORDERED,")
  // - Converts colon-based list lead-ins to em-dash appositives
  // - Ensures first letter of every sentence (after a period) is capitalized
  // - Ensures terminal period
  // NOTE: Does NOT alter the first character of the string — writeClause handles
  // first-letter casing relative to the "ORDERED, " prefix it prepends.
  function cleanClause(text) {
    if (!text) return '';
    var s = String(text).trim().replace(/\s+/g, ' ');
    // Capitalize defined legal terms when used as labels
    s = s.replace(/\b(plaintiff|defendant)\b/g, function (m) {
      return m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
    });
    // Remove duplicate adjacent words (case-insensitive), including around punctuation
    var prev;
    do {
      prev = s;
      s = s.replace(/\b(\w+)(\s*[,;:]\s+)\1(\s*[,;:]\s+)/gi, '$1$3');
      s = s.replace(/\b(\w+)\s+\1\b/gi, '$1');
    } while (s !== prev);
    // Rule 8 — Redundancy Reduction. Collapse common multi-word repetitions that arise when
    // templated fragments are concatenated (e.g., "the minor child the minor child",
    // "provisions contained herein ... provisions contained herein" in the same clause).
    var multiPhrases = [
      'the minor child\\\'s',
      'the minor child',
      'provisions contained herein',
      'in accordance with the applicable state child support guidelines',
      'as soon as reasonably possible or when it is safe to do so',
    ];
    multiPhrases.forEach(function (p) {
      // Collapse immediate adjacency ("X X" -> "X"), allowing punctuation/space between.
      var adj = new RegExp('(' + p + ')(\\s*[,;:]?\\s+)(' + p + ')', 'gi');
      do { prev = s; s = s.replace(adj, '$1$2'); } while (s !== prev && false); // adj only collapses adjacency once
      s = s.replace(adj, '$1');
    });
    // Replace mid-sentence colon list lead-ins with em-dash (preserves time like "3:00")
    s = s.replace(/([a-zA-Z)]):\s+/g, '$1\u2014');
    // Trailing colon becomes period
    s = s.replace(/([a-zA-Z)]):\s*$/g, '$1.');
    // Capitalize first letter of each sentence AFTER a period (not start of string)
    s = s.replace(/([.!?]\s+)([a-z])/g, function (_, p, c) { return p + c.toUpperCase(); });
    // Final whitespace normalization
    s = s.replace(/\s+/g, ' ').trim();
    // Ensure terminal punctuation
    if (s && !/[.!?]$/.test(s)) s += '.';
    return s;
  }

  function checkPage(need) {
    if (y > ph - footerZone - (need || 10)) { pdf.addPage(); y = addPdfHeader(pdf); }
  }
  function setBodyFont(size, style) {
    pdf.setFontSize(size || 10);
    pdf.setFont('times', style || 'normal');
    pdf.setTextColor(0, 0, 0);
  }
  function writeLine(text, opts) {
    opts = opts || {};
    var fs = opts.size || 10;
    var style = opts.style || 'normal';
    var align = opts.align || 'left';
    var color = opts.color || [0, 0, 0];
    pdf.setFontSize(fs);
    pdf.setFont('times', style);
    pdf.setTextColor(color[0], color[1], color[2]);
    var maxW = opts.maxW || contentW;
    var lines = pdf.splitTextToSize(text, maxW);
    var xPos = align === 'center' ? pw / 2 : margin;
    lines.forEach(function (line) {
      checkPage(fs * 0.7);
      pdf.text(line, xPos, y, { align: align });
      y += fs * 0.7;
    });
  }
  function addSpace(pts) { y += pts || 4; }
  function drawHLine() {
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
    pdf.line(margin, y, pw - margin, y); y += 2;
  }

  // ===== PAGE 1 HEADER =====
  y = addPdfHeader(pdf);
  addSpace(2);

  // ===== COURT NAME & LOCATION =====
  var courtName = planData.courtName || 'IN THE COURT';
  writeLine(courtName.toUpperCase(), { size: 12, style: 'bold', align: 'center' });
  addSpace(1);
  if (planData.courtAddress) {
    writeLine(planData.courtAddress.toUpperCase(), { size: 10, style: 'normal', align: 'center' });
  }
  addSpace(4);

  // ===== CASE CAPTION TABLE =====
  var captionTop = y;
  var dividerX = pw * 0.62;
  var captionBottom = captionTop;

  // Left side — Parties
  pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
  var leftX = margin + 4;
  var leftY = captionTop + 5;

  // Plaintiff block
  function writePartyBlock(name, addr, city, st, zip, phone, email, role, startY) {
    var py = startY;
    if (name) { pdf.setFont('times', 'bold'); pdf.text(name, leftX, py); py += 4.5; }
    pdf.setFont('times', 'normal');
    if (addr) { pdf.text(addr, leftX, py); py += 4; }
    var cityLine = [city, st, zip].filter(Boolean).join(', ');
    if (cityLine) { pdf.text(cityLine, leftX, py); py += 4; }
    if (phone) { pdf.text(phone, leftX, py); py += 4; }
    if (email) { pdf.text(email, leftX, py); py += 4; }
    pdf.setFont('times', 'bolditalic');
    pdf.text(role, leftX, py); py += 5;
    pdf.setFont('times', 'normal');
    return py;
  }

  leftY = writePartyBlock(
    titleCaseName(planData.plaintiffName), planData.plaintiffAddress,
    planData.plaintiffCity, planData.plaintiffState, planData.plaintiffZip,
    planData.plaintiffPhone, planData.plaintiffEmail, 'Plaintiff', leftY
  );

  // "v." separator
  leftY += 2;
  pdf.setFont('times', 'normal'); pdf.setFontSize(10);
  pdf.text('v.', leftX + 30, leftY); leftY += 6;

  // Defendant block
  leftY = writePartyBlock(
    titleCaseName(planData.defendantName), planData.defendantAddress,
    planData.defendantCity, planData.defendantState, planData.defendantZip,
    planData.defendantPhone, planData.defendantEmail, 'Defendant', leftY
  );
  leftY += 2;

  // Right side — Case Number
  var rightX = dividerX + 6;
  pdf.setFont('times', 'normal'); pdf.setFontSize(10);
  pdf.text('Case No.: ' + (planData.caseNumber || '_____________'), rightX, captionTop + 8);

  // Draw caption box
  captionBottom = Math.max(leftY, captionTop + 30);
  pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
  pdf.rect(margin, captionTop, contentW, captionBottom - captionTop);
  // Vertical divider
  pdf.line(dividerX, captionTop, dividerX, captionBottom);

  y = captionBottom + 6;

  // ===== WITHOUT PREJUDICE LABEL =====
  var isWithoutPrejudice = ['settlement', 'mediation', 'consent'].includes(planData.filingContext);
  if (isWithoutPrejudice) {
    writeLine('*** WITHOUT PREJUDICE FOR SETTLEMENT PURPOSES ONLY ***', { size: 10, style: 'bold', align: 'center' });
    addSpace(4);
  }

  // ===== DOCUMENT TITLE =====
  var party = planData.submittingParty === 'defendant' ? "Defendant's" : "Plaintiff's";
  var docTitle = 'Parenting Plan';
  if (planData.filingContext === 'court') {
    docTitle = party + ' Proposed Parenting Plan';
  } else if (planData.filingContext === 'mediation') {
    docTitle = party + ' Parenting Plan Submission for Mediation';
  } else if (planData.filingContext === 'settlement') {
    docTitle = party + ' Parenting Plan Settlement Submission';
  } else if (planData.filingContext === 'consent') {
    docTitle = party + ' Proposed Parenting Plan for Consent Order';
  }

  pdf.setFont('times', 'bold'); pdf.setFontSize(13);
  var titleW = pdf.getTextWidth(docTitle);
  var titleX = (pw - titleW) / 2;
  pdf.text(docTitle, pw / 2, y, { align: 'center' });
  // Underline
  pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.4);
  pdf.line(titleX, y + 1, titleX + titleW, y + 1);
  y += 8;

  // ===== CASE INFORMATION TABLE =====
  // Must appear AFTER the document title and BEFORE any substantive parenting plan clauses.
  // This is strictly informational — no interpretation, no legal prose.
  addSpace(3);
  checkPage(48);
  // Section heading (plain, not numbered — this is metadata, not a substantive section)
  pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
  pdf.text('Case Information', margin, y);
  y += 2;
  pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3);
  pdf.line(margin, y, pw - margin, y);
  y += 4;

  var ciColW1 = contentW * 0.4;
  var ciColW2 = contentW * 0.6;
  var ciRowH = 7;
  var ciX = margin;

  // Build rows from user inputs (direct mapping, no inference)
  var relCasesInput = (planData.relatedCases || []).filter(function (c) {
    return c && (String(c.caseNumber || '').trim() || String(c.caseType || '').trim());
  });
  var relatedCasesValue;
  if (relCasesInput.length === 0) {
    relatedCasesValue = 'None';
  } else {
    relatedCasesValue = relCasesInput.map(function (c) {
      var num = String(c.caseNumber || '').trim();
      var typ = String(c.caseType || '').trim();
      if (num && typ) return num + ' (' + typ + ')';
      return num || typ;
    }).join('; ');
  }

  var caseInfoRows = [
    { label: 'Original Filing or Modification', value: planData.existingOrder === 'yes' ? 'Modification' : 'Original Order' },
    { label: 'Active Protective Order', value: planData.protectiveOrder === 'yes' ? 'True' : 'False' },
    { label: 'Supervised Visitation', value: planData.supervisedVisitation === 'yes' ? 'True' : 'False' },
    { label: 'Related Cases', value: relatedCasesValue },
  ];

  // Render table rows with borders
  caseInfoRows.forEach(function (row) {
    // Compute wrapped height for the value column
    pdf.setFont('times', 'normal'); pdf.setFontSize(10);
    var valLines = pdf.splitTextToSize(row.value, ciColW2 - 6);
    var rowHeight = Math.max(ciRowH, valLines.length * 5 + 2);
    checkPage(rowHeight + 2);

    // Label cell (bold)
    pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
    pdf.text(row.label, ciX + 3, y + 5);

    // Value cell
    pdf.setFont('times', 'normal');
    var vy = y + 5;
    valLines.forEach(function (ln) {
      pdf.text(ln, ciX + ciColW1 + 3, vy);
      vy += 5;
    });

    // Borders (matched to caption table)
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
    pdf.rect(ciX, y, contentW, rowHeight);
    pdf.line(ciX + ciColW1, y, ciX + ciColW1, y + rowHeight);
    y += rowHeight;
  });
  addSpace(4);

  // ===== SECTION HELPER =====
  var sectionNum = 0;
  var ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'];
  function addSection(title) {
    addSpace(5);
    checkPage(14);
    sectionNum++;
    var heading = ROMAN[sectionNum - 1] + '. ' + title.toUpperCase();
    pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
    pdf.text(heading, margin, y); y += 2;
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3);
    pdf.line(margin, y, pw - margin, y); y += 5;
    setBodyFont(10);
  }
  function writeClause(text) {
    if (!text) return;
    checkPage(8);
    pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
    // Strip ALL pre-existing "ORDERED," lead-ins so the prefix isn't duplicated
    var body = String(text).replace(/^(\s*ORDERED\s*,\s*)+/i, '');
    // Enforce grammar, duplicate-word, capitalization, and colon rules
    body = cleanClause(body);
    if (!body) return;
    // Lowercase the first letter of body if it's a common word (not a proper noun).
    // The prefix "ORDERED, " creates a single sentence that flows in lowercase,
    // e.g., "ORDERED, the parties shall...". Proper nouns keep their caps.
    var firstWord = (body.match(/^\S+/) || [''])[0].replace(/[^A-Za-z]/g, '');
    var properNounStart =
      firstWord === 'Plaintiff' ||
      firstWord === 'Defendant' ||
      (pName && pName !== 'Plaintiff' && body.indexOf(pName) === 0) ||
      (dName && dName !== 'Defendant' && body.indexOf(dName) === 0) ||
      (childName && childName !== 'the minor child' && body.indexOf(childName) === 0) ||
      /^[A-Z][A-Z]/.test(firstWord); // Acronyms (e.g., "IRS")
    if (!properNounStart && /^[A-Z][a-z]/.test(body)) {
      body = body.charAt(0).toLowerCase() + body.slice(1);
    }
    // Prefix with bold "ORDERED,"
    var prefix = 'ORDERED, ';
    pdf.setFont('times', 'bold');
    var prefixW = pdf.getTextWidth(prefix);
    var allText = prefix + body;
    var allLines = pdf.splitTextToSize(allText, contentW);
    allLines.forEach(function (line, idx) {
      checkPage(5);
      if (idx === 0) {
        // First line: bold prefix + normal text
        pdf.setFont('times', 'bold');
        pdf.text(prefix, margin, y);
        pdf.setFont('times', 'normal');
        pdf.text(line.substring(prefix.length), margin + prefixW, y);
      } else {
        pdf.setFont('times', 'normal');
        pdf.text(line, margin, y);
      }
      y += 5.3;
    });
    addSpace(2);
  }
  function writeField(label, val) {
    if (!val) return;
    checkPage(6);
    pdf.setFont('times', 'bold'); pdf.setFontSize(10);
    pdf.text(label + ':', margin, y);
    var labelW = pdf.getTextWidth(label + ': ');
    pdf.setFont('times', 'normal');
    var valLines = pdf.splitTextToSize(String(val), contentW - labelW);
    pdf.text(valLines[0], margin + labelW, y);
    y += 5.3;
    for (var i = 1; i < valLines.length; i++) {
      checkPage(5);
      pdf.text(valLines[i], margin + 4, y);
      y += 5.3;
    }
    addSpace(1);
  }

  // ===== HELPER: Authority clause with tie-breaker integration =====
  // Rule: When joint + tie-breaker are both selected, merge into ONE unified clause.
  // Structure: joint authority → good faith consultation → deadlock condition → tie-breaker assignment
  function authorityClause(topic, val, tiebreakerVal) {
    if (!val) return;
    var pName = planData.plaintiffName || 'Plaintiff';
    var dName = planData.defendantName || 'Defendant';
    if (val === 'joint' && tiebreakerVal) {
      var tbName = tiebreakerVal === 'plaintiff' ? pName + ' (Plaintiff)' : tiebreakerVal === 'defendant' ? dName + ' (Defendant)' : 'a qualified mediator selected by the parties';
      writeClause('ORDERED, the parties shall share joint decision-making authority regarding ' + topic + ', except as otherwise provided herein. The parties shall consult with one another in good faith in an effort to reach mutual agreement on all matters related to ' + topic + '. In the event the parties are unable to reach agreement after reasonable consultation, ' + tbName + ' shall have tie-breaker authority to make the final decision regarding ' + topic + '.');
    } else if (val === 'joint') {
      writeClause('ORDERED, the parties shall share joint decision-making authority regarding ' + topic + '. The parties shall consult with one another in good faith and cooperate in making all decisions related to ' + topic + '. Neither party shall make a unilateral decision regarding ' + topic + ' without first consulting the other party.');
    } else if (val === 'plaintiff' || val === 'sole-plaintiff') {
      writeClause('ORDERED, ' + pName + ' (Plaintiff) shall have sole decision-making authority regarding ' + topic + '. ' + pName + ' (Plaintiff) shall have the exclusive right to make all decisions related to ' + topic + ' without the requirement of consent or agreement from ' + dName + ' (Defendant).');
    } else if (val === 'defendant' || val === 'sole-defendant') {
      writeClause('ORDERED, ' + dName + ' (Defendant) shall have sole decision-making authority regarding ' + topic + '. ' + dName + ' (Defendant) shall have the exclusive right to make all decisions related to ' + topic + ' without the requirement of consent or agreement from ' + pName + ' (Plaintiff).');
    } else if (val === 'routine-custodial') {
      writeClause('ORDERED, routine decisions regarding ' + topic + ' shall be made by the parent exercising parenting time at the time such decision is required. Major decisions regarding ' + topic + ' shall require the joint agreement of both parties. The parties shall consult with one another in good faith before making any major decision related to ' + topic + '.');
    } else if (val === 'mediator') {
      writeClause('ORDERED, the parties shall share joint decision-making authority regarding ' + topic + '. The parties shall consult with one another in good faith in an effort to reach mutual agreement. In the event the parties are unable to reach agreement after reasonable consultation regarding ' + topic + ', the matter shall be submitted to a qualified mediator for resolution. The cost of such mediation shall be allocated as set forth elsewhere in this Parenting Plan or as the parties may agree.');
    } else {
      writeClause('The parties shall determine matters related to ' + topic + ' as agreed, in accordance with applicable law.');
    }
  }

  // ===== CHILDREN =====
  var rawChildName = (planData.children && planData.children[0]) ? planData.children[0].name : '';
  var childName = rawChildName ? titleCaseName(rawChildName) : 'the minor child';
  var childDob = (planData.children && planData.children[0]) ? planData.children[0].dob : '';
  var pName = titleCaseName(planData.plaintiffName) || 'Plaintiff';
  var dName = titleCaseName(planData.defendantName) || 'Defendant';

  // ===== I. CHILDREN =====
  addSection('CHILDREN');
  if (childName !== 'the minor child') {
    writeClause('This Parenting Plan pertains to the physical and legal custody of the minor child ' + childName + (childDob ? ', born ' + childDob : '') + ', who shall be referred to herein as the minor child, and governs the rights and responsibilities of the parties with respect to the care, control, and welfare of the minor child.');
  } else {
    writeClause('This Parenting Plan pertains to the physical and legal custody of the minor child of the parties and governs the rights and responsibilities of the parties with respect to the care, control, and welfare of the minor child.');
  }

  // ===== II. CUSTODY =====
  addSection('CUSTODY ARRANGEMENT');
  if (planData.physicalCustody === 'joint') {
    writeClause('The parties shall share joint physical custody of ' + childName + ', and the minor child shall reside with each party in accordance with the parenting time schedule set forth in this Parenting Plan. Each party shall exercise primary care and control of the minor child during their respective parenting time, subject to the provisions contained herein governing the welfare of the minor child.');
  } else if (planData.physicalCustody === 'sole-plaintiff') {
    writeClause(pName + ' (Plaintiff) shall have sole physical custody of ' + childName + ', and the minor child shall primarily reside with ' + pName + ' (Plaintiff), subject to the parenting time rights of ' + dName + ' (Defendant) as set forth in this Parenting Plan. ' + dName + ' (Defendant) shall exercise parenting time in accordance with the schedule and the provisions contained herein.');
  } else if (planData.physicalCustody === 'sole-defendant') {
    writeClause(dName + ' (Defendant) shall have sole physical custody of ' + childName + ', and the minor child shall primarily reside with ' + dName + ' (Defendant), subject to the parenting time rights of ' + pName + ' (Plaintiff) as set forth in this Parenting Plan. ' + pName + ' (Plaintiff) shall exercise parenting time in accordance with the schedule and the provisions contained herein.');
  } else {
    // Fallback so the section always carries a substantive custody clause,
    // even when the user has not explicitly picked a physical custody option.
    writeClause('The parties shall share physical custody of ' + childName + ' in accordance with the parenting time schedule set forth in this Parenting Plan, and each party shall exercise primary care and control of the minor child during their respective parenting time.');
  }
  authorityClause('legal custody and major life decisions for ' + childName, planData.legalCustody);

  // Rule 13 — Supremacy Clause (custody) when modifying an existing order.
  if (planData.existingOrder === 'yes') {
    writeClause('The terms of this Order shall supersede all prior orders of this Court regarding the custody of the minor child, and any provision of any prior order inconsistent with this Parenting Plan shall be deemed modified to the extent necessary to conform to this Order.');
  }

  // ===== III. SAFETY & RESTRICTIONS =====
  if (planData.protectiveOrder === 'yes' || planData.dvHistory === 'yes' || planData.supervisedVisitation === 'yes') {
    addSection('SAFETY AND RESTRICTIONS');
    if (planData.protectiveOrder === 'yes') {
      writeClause('The parties acknowledge that a protective order is currently in effect. Any subsequently entered Parenting Plan or child custody order shall supersede and control over any conflicting child custody, parenting time, visitation, exchange, or co-parenting provisions contained within the protective order, unless otherwise ordered by a court of competent jurisdiction. All remaining non-conflicting provisions of the protective order shall remain in effect unless modified or terminated by the Court.');
      writeClause('Any provision of this Parenting Plan permitting communication between the parties shall supersede only those no-contact provisions of the protective order to the limited extent necessary for communication regarding the child, the child\u2019s welfare, parenting time, exchanges, emergencies, education, medical matters, extracurricular activities, or other child-related issues. The parties are otherwise ordered to remain no contact with one another for all non-child-related matters unless otherwise authorized by further order of the Court.');
    }
    if (planData.dvHistory === 'yes') writeClause('The parties acknowledge a history of domestic violence. All exchanges and communications shall be conducted in accordance with applicable safety protocols and any existing court orders.');
    if (planData.supervisedVisitation === 'yes') {
      var svParts = ['Parenting time for the non-custodial parent shall be supervised'];
      var svFacilityName = (planData.supervisedVisitationFacilityName || '').trim();
      var svAddr = (planData.supervisedVisitationLocation || '').trim();
      if (svFacilityName && svAddr) {
        svParts.push('at ' + svFacilityName + ', located at ' + svAddr);
      } else if (svFacilityName) {
        svParts.push('at ' + svFacilityName);
      } else if (svAddr) {
        svParts.push('at ' + svAddr);
      }
      if (planData.supervisedVisitationSupervisor) svParts.push('under the supervision of ' + planData.supervisedVisitationSupervisor);
      writeClause(svParts.join(' ') + '.');
      if (planData.supervisedVisitationSchedule || planData.supervisedVisitationDay || planData.supervisedVisitationTime || planData.supervisedVisitationDuration) {
        var schedParts = ['Supervised visitation shall occur'];
        if (planData.supervisedVisitationSchedule) schedParts.push(planData.supervisedVisitationSchedule);
        if (planData.supervisedVisitationDay) schedParts.push('on ' + planData.supervisedVisitationDay);
        if (planData.supervisedVisitationTime) schedParts.push('at ' + planData.supervisedVisitationTime);
        if (planData.supervisedVisitationDuration) schedParts.push('for a duration of ' + planData.supervisedVisitationDuration);
        writeClause(schedParts.join(', ') + '.');
      }
    }
  }

  // ===== IV. COMMUNICATION =====
  addSection('COMMUNICATION');
  var commLabels = {
    'app-only': 'All communication between the parties regarding ' + childName + ' and any matter affecting the welfare of the minor child shall occur exclusively through the Parentum co-parenting application, which shall serve as the sole channel for written communication between the parties.',
    'email': 'All communication between the parties regarding ' + childName + ' shall be conducted via email, which shall preserve a written record of all exchanges for documentation and enforcement purposes.',
    'text-email': 'The parties shall communicate via text message for time-sensitive matters concerning ' + childName + ', and via email for significant decisions and any communication intended to serve as a formal record.',
    'usps': 'All formal written communication between the parties regarding ' + childName + ' shall be sent via United States Postal Service with delivery confirmation, with routine scheduling matters permitted through any reasonable written method.',
    'third-party': 'All communication between the parties shall be facilitated through a mutually designated third party, who shall relay information and requests concerning ' + childName + ' between the parties.',
    'any': 'The parties may communicate regarding ' + childName + ' through any reasonable method, provided such communication is respectful and directed to matters concerning the minor child.'
  };
  if (planData.preferredComm && commLabels[planData.preferredComm]) {
    writeClause(commLabels[planData.preferredComm]);
  }
  // Rule 10A — Scope + Tone restriction applied universally.
  writeClause('All communication between the parties shall be limited in scope to matters directly concerning ' + childName + ' and the provisions contained herein. Neither party shall disparage, demean, mock, or speak negatively about the other party directly or indirectly with the minor child, and each party shall conduct themselves in a respectful, child-focused manner in all communications.');
  if (planData.preferredComm === 'app-only' && planData.appCostPayer) {
    var costText = { plaintiff: pName + ' (Plaintiff) shall pay for both parties\' Parentum Co-Parenting Application accounts.', defendant: dName + ' (Defendant) shall pay for both parties\' Parentum Co-Parenting Application accounts.', split: 'Each party shall be responsible for the cost and creation of their own Parentum Co-Parenting Application account.' };
    if (costText[planData.appCostPayer]) writeClause(costText[planData.appCostPayer]);
  }
  if (planData.commTimeframe) {
    var tfLabels = { '24h': 'twenty-four (24) hours', '48h': 'forty-eight (48) hours', '72h': 'seventy-two (72) hours', 'business': 'two (2) business days' };
    var tfText = tfLabels[planData.commTimeframe] || planData.commTimeframe.replace(/-/g, ' ');
    writeClause('Each party shall respond to non-emergency communications concerning ' + childName + ' within ' + tfText + '. Failure to respond within this timeframe may be considered a waiver of objection on the matter communicated.');
  }
  // Standard for every Parenting Plan: non-response protection for non-child matters.
  // Always rendered, regardless of which Communication options were selected.
  writeClause('Failure to respond to communications unrelated to the minor child shall not be construed as a failure to co-parent.');
  if (planData.emergencyComm) {
    var emCommMap = {
      'call': 'In the event of an emergency involving ' + childName + ', each party shall promptly notify the other by direct telephone call as soon as reasonably possible or when it is safe to do so. If the other party does not answer, a voicemail and a follow-up text message providing the nature and location of the emergency shall be sent immediately.',
      'text': 'In the event of an emergency involving ' + childName + ', each party shall promptly notify the other by text message as soon as reasonably possible or when it is safe to do so. The notifying party shall provide sufficient detail regarding the nature, location, and severity of the emergency and the current condition of the minor child.',
      'both': 'In the event of an emergency involving ' + childName + ', each party shall promptly notify the other using both telephone contact and written communication as soon as reasonably possible or when it is safe to do so. The notifying party shall first attempt to reach the other party by telephone call and shall also send a text message or written notification providing details of the emergency and the current condition of the minor child.'
    };
    writeClause(emCommMap[planData.emergencyComm] || 'In the event of an emergency involving ' + childName + ', each party shall promptly notify the other using all reasonably available methods of communication as soon as reasonably possible or when it is safe to do so.');
  }
  if (planData.commBoundaries && planData.commBoundaries.length > 0) {
    var boundaryMap = {
      'child-only': 'The parties shall limit their communications to issues directly concerning ' + childName + ' and matters reasonably necessary to implement this Parenting Plan. Communications regarding personal matters unrelated to the child are prohibited.',
      'no-disparage': 'Neither party shall disparage, demean, mock, or speak negatively about the other party in communications concerning ' + childName + '. Neither party shall use co-parent communications to insult, provoke, harass, or intimidate the other.',
      'written-record': 'Except in emergencies or where impracticable, the parties shall communicate in writing or through a communication platform that preserves a written record. This requirement ensures that significant decisions and agreements are documented for future reference.'
    };
    planData.commBoundaries.forEach(function(bId) {
      if (boundaryMap[bId]) writeClause(boundaryMap[bId]);
    });
  }
  if (planData.thirdParties && planData.thirdParties.filter(function(t) { return t.trim(); }).length > 0) {
    var tpList = planData.thirdParties.filter(function(t) { return t.trim(); });
    var tpJoined = tpList.length === 1 ? tpList[0] : tpList.length === 2 ? tpList.join(' and ') : tpList.slice(0, -1).join(', ') + ', and ' + tpList[tpList.length - 1];
    writeClause('The parties designate ' + tpJoined + ' as authorized third parties who may facilitate communication and custody exchanges between the parties.');
  }
  if (planData.phoneCallsEnabled && planData.phoneCallFrequency) {
    var freqText = planData.phoneCallFrequency === 'daily' ? 'each day' : 'each week';
    var dayText = '';
    if (planData.phoneCallFrequency === 'weekly' && planData.phoneCallDays && planData.phoneCallDays.length > 0) {
      dayText = ' on ' + planData.phoneCallDays.join(', ');
    }
    var timeText = planData.phoneCallTime ? ' at ' + planData.phoneCallTime : ' at a mutually agreed-upon time';
    writeClause('The non-custodial parent shall be entitled to regular telephone or video contact with ' + childName + ' ' + freqText + dayText + timeText + '. The custodial parent shall ensure that ' + childName + ' is available and in a private, comfortable setting for such calls.');
    if (planData.phoneCallRecording) {
      var recordMap = {
        'no-recording': 'Neither party shall record, monitor, or listen in on telephone or video calls between ' + childName + ' and the other parent. Any recording of such calls is strictly prohibited absent a separate Court order.',
        'mutual-consent': 'Telephone or video calls between ' + childName + ' and the other parent may be recorded only with the prior written consent of both parties. Absent mutual consent, no recording shall be made.',
        'one-party': 'Either parent may record telephone or video calls between ' + childName + ' and the other parent in accordance with applicable one-party consent recording laws. The recording party need not obtain consent from the other party, provided such recording is lawful in the jurisdiction where the recording party is located.',
        'state-law': 'Recording of telephone or video calls between ' + childName + ' and the other parent shall be permitted to the extent allowed by the recording laws of the state where the recording party is located at the time of the call. Each party is solely responsible for determining and complying with applicable recording consent requirements.',
      };
      if (recordMap[planData.phoneCallRecording]) writeClause(recordMap[planData.phoneCallRecording]);
    } else {
      writeClause('Neither parent shall monitor, record, or interfere with calls between the child and the other parent unless otherwise ordered by the Court.');
    }
  }

  // ===== V. HEALTH & EMERGENCY =====
  addSection('HEALTH AND EMERGENCY');
  authorityClause('medical decisions for ' + childName, planData.medicalDecisions);
  if (planData.yearlyCheckupScheduler) {
    var schedLabels = { plaintiff: pName + ' (Plaintiff)', defendant: dName + ' (Defendant)', joint: 'Both parties jointly' };
    writeClause((schedLabels[planData.yearlyCheckupScheduler] || 'The designated party') + ' shall be responsible for scheduling yearly medical checkups for ' + childName + '.');
  }
  if (planData.yearlyCheckupAttendance) {
    var attendMap = {
      'both': 'Both parents shall be permitted to attend all yearly medical checkups for ' + childName + ', regardless of which parent scheduled the appointment.',
      'scheduling-parent': 'Only the parent who scheduled the yearly medical checkup shall attend the appointment. The scheduling parent shall promptly provide the other parent with a written summary of the appointment, including any diagnoses, recommendations, and follow-up instructions.',
      'custodial': 'Only the parent exercising custody at the time of the yearly medical checkup shall attend the appointment. That parent shall promptly provide the other parent with a written summary of the appointment results.',
      'either': 'Either parent may independently attend yearly medical checkups for ' + childName + '. Each parent shall inform the other of any scheduled appointments and share all medical information obtained.'
    };
    writeClause(attendMap[planData.yearlyCheckupAttendance] || 'Attendance at yearly medical checkups for ' + childName + ' shall be determined as agreed by the parties.');
  }
  if (planData.unforeseenVisitScheduler) {
    var unfLabels = { 'custodial-parent': 'The parent exercising custody or access at that time may schedule and attend unforeseen medical appointments.', 'either-notify': 'Either parent may schedule unforeseen medical appointments, provided prompt notification is given to the other parent.', 'joint-approval': 'Unforeseen medical appointments shall require joint approval of both parties, except in the case of a medical emergency.' };
    writeClause(unfLabels[planData.unforeseenVisitScheduler] || planData.unforeseenVisitScheduler);
  }
  // Emergency — Rule 10B: timing + method + safety qualifier
  if (planData.emergencyDecision) {
    var emLabels = {
      either: 'In the event of a medical emergency involving ' + childName + ', either party may authorize emergency medical treatment in the child\'s immediate best interests and shall notify the other party as soon as reasonably possible or when it is safe to do so, providing details of the emergency, the treating facility, and the condition of the minor child.',
      'notify-asap': 'In the event of a medical emergency involving ' + childName + ', either party may authorize emergency medical treatment in the child\'s immediate best interests and shall notify the other party as soon as reasonably possible or when it is safe to do so, providing details of the emergency, the treating facility, and the condition of the minor child.',
      'notify-24h': 'In the event of a medical emergency involving ' + childName + ', either party may authorize emergency medical treatment in the child\'s immediate best interests and shall notify the other party within twenty-four (24) hours of the commencement of treatment, or as soon as reasonably possible or when it is safe to do so, providing details of the emergency, the treating facility, and the condition of the minor child.'
    };
    writeClause(emLabels[planData.emergencyDecision] || planData.emergencyDecision);
  } else {
    writeClause('In the event of a medical emergency involving ' + childName + ', either party may act in the child\'s immediate best interests and authorize emergency treatment, and shall notify the other party as soon as reasonably possible or when it is safe to do so, providing details of the emergency and the condition of the minor child.');
  }
  if (planData.healthInsurance) {
    var insLabels = { plaintiff: pName + ' (Plaintiff) shall provide and maintain health, dental, and vision insurance coverage for ' + childName + '. The cost of such insurance premiums shall be borne solely by ' + pName + ' (Plaintiff).', defendant: dName + ' (Defendant) shall provide and maintain health, dental, and vision insurance coverage for ' + childName + '. The cost of such insurance premiums shall be borne solely by ' + dName + ' (Defendant).', 'better-plan': 'The party with the better available health, dental, and vision insurance plan shall provide and maintain coverage for ' + childName + '. The parties shall cooperate in determining which plan offers superior coverage. The party providing coverage shall bear the cost of the insurance premiums unless otherwise agreed in writing.', shared: 'The parties shall share the cost of health, dental, and vision insurance premiums for ' + childName + ', with each party responsible for fifty percent (50%) of the total premium cost. Both parties shall cooperate in selecting and maintaining appropriate coverage.' };
    writeClause(insLabels[planData.healthInsurance] || 'Health insurance for ' + childName + ' shall be provided as agreed by the parties.');
  }
  authorityClause('mental health and counseling decisions for ' + childName, planData.mentalHealthDecisions);
  if (planData.specialNeeds) writeClause('The minor child ' + childName + ' has special health considerations, namely ' + planData.specialNeeds + ', and both parties shall accommodate such considerations during their respective parenting time.');

  // ===== VI. EDUCATION & DEVELOPMENT =====
  addSection('EDUCATION AND DEVELOPMENT');
  if (planData.schoolDistrictResidence) {
    var distLabels = { plaintiff: pName + "'s (Plaintiff's) residence", defendant: dName + "'s (Defendant's) residence", joint: 'a residence determined jointly by the parties' };
    writeClause('The school district for ' + childName + ' shall be determined by ' + (distLabels[planData.schoolDistrictResidence] || 'the parties') + '.');
  }
  authorityClause('school selection and enrollment for ' + childName, planData.schoolChoice);
  if (planData.schoolType) {
    var typeLabels = { public: 'public school', private: 'private school', charter: 'charter school', homeschool: 'homeschool' };
    writeClause(childName + ' shall attend ' + (typeLabels[planData.schoolType] || planData.schoolType) + ', unless otherwise agreed by the parties.');
  }
  authorityClause('major academic decisions for ' + childName, planData.academicAuthority, planData.academicTiebreaker);
  if (planData.iep504Plan) {
    var iepLabels = { 'joint-participation': 'Both parents shall participate in all IEP/504 meetings and decisions.', 'plaintiff-lead': pName + ' (Plaintiff) shall take the lead role in IEP/504 processes and shall keep ' + dName + ' (Defendant) informed.', 'defendant-lead': dName + ' (Defendant) shall take the lead role in IEP/504 processes and shall keep ' + pName + ' (Plaintiff) informed.' };
    writeClause(iepLabels[planData.iep504Plan] || planData.iep504Plan);
  }
  if (planData.extracurriculars) {
    var extraLabels = { 'joint-approval': 'The parties shall jointly approve all extracurricular activities for ' + childName + '. Neither party shall unilaterally enroll ' + childName + ' in any extracurricular activity without the other party\'s consent.', 'each-during': 'Each party may independently enroll ' + childName + ' in extracurricular activities during that party\'s parenting time, provided such activities do not materially interfere with the other party\'s parenting time.', 'discuss-then-enroll': 'The parties shall discuss extracurricular activities in good faith before enrollment. Either party may then proceed with enrollment after reasonable consultation.', 'cost-cap': 'The parties shall jointly approve all extracurricular activities for ' + childName + '. Both parties shall agree on a per-activity cost limit, and any activity exceeding that limit shall require express written consent of both parties.' };
    writeClause(extraLabels[planData.extracurriculars] || 'The parties shall determine ' + childName + '\'s participation in extracurricular activities as agreed.');
  }
  if (planData.afterSchoolClubs) {
    var clubLabels = { 'joint-approval': 'The parties shall jointly approve all non-school enrichment activities for ' + childName + ', including but not limited to sports, music lessons, dance, gymnastics, and cheer. Neither party shall unilaterally enroll ' + childName + ' in such activities without the other party\'s consent.', 'either-parent': 'Either party may enroll ' + childName + ' in non-school enrichment activities, including but not limited to sports, music lessons, dance, gymnastics, and cheer, provided the enrolling party bears the initial cost and notifies the other party promptly.', 'custodial-decides': 'The custodial parent at the time of the activity shall have decision-making authority regarding non-school enrichment activities for ' + childName + ', including but not limited to sports, music lessons, dance, gymnastics, and cheer.', 'cost-threshold': 'Non-school enrichment activities for ' + childName + ' that exceed the agreed cost threshold shall require joint approval of both parties. Activities below the threshold may be enrolled by either party independently.' };
    writeClause(clubLabels[planData.afterSchoolClubs] || 'The parties shall determine ' + childName + '\'s participation in non-school activities as agreed.');
  }
  if (planData.activityCostCap) writeClause('Any individual extracurricular or non-school activity exceeding ' + planData.activityCostCap + ' dollars ($' + planData.activityCostCap + ') per activity or per season shall require the express joint approval of both parties before enrollment.');
  if (planData.religiousUpbringing) {
    var relLabels = { joint: 'Decisions regarding religious or spiritual upbringing shall be made jointly.', plaintiff: pName + ' (Plaintiff) shall make decisions regarding religious upbringing.', defendant: dName + ' (Defendant) shall make decisions regarding religious upbringing.', 'exposure-both': childName + ' shall be exposed to both parties\' religious traditions.', none: 'No specific religious instruction shall be required.' };
    writeClause(relLabels[planData.religiousUpbringing] || planData.religiousUpbringing);
  }

  // ===== VII. PARENTING TIME SCHEDULE =====
  addSection('PARENTING TIME SCHEDULE');
  if (planData.schedulePattern) {
    var patLabels = { eow: 'Every Other Weekend (EOW)', '2-2-3': '2-2-3 Rotating Pattern', '3-4': '3-4-4-3 Rotating Pattern', 'week-on': 'Week On / Week Off', '5-2': '5-2 Split', custom: 'Custom Schedule' };
    writeClause('The parties shall follow a ' + (patLabels[planData.schedulePattern] || planData.schedulePattern) + ' parenting time schedule as set forth below.');
  }

  // Out-of-state parent
  if (planData.parentOutOfState === 'yes') {
    var oosParent = planData.outOfStateParent === 'defendant' ? dName + ' (Defendant)' : pName + ' (Plaintiff)';
    writeClause(oosParent + ' resides out of state. Due to the geographic distance between the parties, the following school year and summer schedules shall govern parenting time in lieu of the standard weekly rotation.');
    if (planData.schoolYearSchedule) writeClause('During the school year, the parenting time schedule shall be governed by the following terms, namely ' + planData.schoolYearSchedule + '.');
    if (planData.summerSchedule) writeClause('During the summer months, the parenting time schedule shall be governed by the following terms, namely ' + planData.summerSchedule + '.');
  }

  // ===== 4-WEEK SCHEDULE TABLE =====
  // Data source is now `weekGridOvernight` (the UR-square control). The legacy
  // `weekGrid` array is kept in state for back-compat but is no longer written to.
  var hasOvernightData = (planData.weekGridOvernight || []).some(function(w) { return w && w.some(function(c) { return c !== ''; }); });
  var hasLegacyGridData = planData.weekGrid.some(function(w) { return w.some(function(c) { return c !== ''; }); });
  var hasGridData = hasOvernightData || hasLegacyGridData;
  if (hasGridData) {
    addSpace(3);
    checkPage(50);
    writeLine('Four-Week Parenting Time Schedule', { size: 10, style: 'bold' });
    addSpace(2);

    var WEEKDAYS_PDF = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var colW = (contentW - 16) / 7;
    var rowH = 10;
    var tableX = margin;
    var headerH = 7;

    // Draw header row
    checkPage(headerH + rowH * 5);
    pdf.setFillColor(230, 230, 230);
    pdf.rect(tableX, y, 16, headerH, 'F');
    pdf.setFont('times', 'bold'); pdf.setFontSize(6.5); pdf.setTextColor(0, 0, 0);
    pdf.text('Week', tableX + 2, y + 4.5);
    WEEKDAYS_PDF.forEach(function(dn, i) {
      var cx = tableX + 16 + i * colW;
      pdf.setFillColor(230, 230, 230);
      pdf.rect(cx, y, colW, headerH, 'F');
      pdf.text(dn, cx + colW / 2, y + 4.5, { align: 'center' });
    });
    // Header border
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
    pdf.rect(tableX, y, contentW, headerH);
    for (var hi = 0; hi <= 7; hi++) {
      var hx = tableX + 16 + hi * colW;
      if (hi < 7) pdf.line(hx, y, hx, y + headerH);
    }
    pdf.line(tableX + 16, y, tableX + 16, y + headerH);
    y += headerH;

    // Data rows
    [0, 1, 2, 3].forEach(function(weekIdx) {
      pdf.setFont('times', 'bold'); pdf.setFontSize(7); pdf.setTextColor(0, 0, 0);
      pdf.text('Week ' + (weekIdx + 1), tableX + 2, y + 6);
      WEEKDAYS_PDF.forEach(function(dn, dayIdx) {
        var legacyVal = planData.weekGrid[weekIdx][dayIdx];
        var overnightVal = (planData.weekGridOvernight && planData.weekGridOvernight[weekIdx]) ? planData.weekGridOvernight[weekIdx][dayIdx] : '';
        // Prefer overnight-driven fill; fall back to legacy grid so older saved plans still render
        var fillRole = overnightVal || legacyVal;
        var cx = tableX + 16 + dayIdx * colW;
        // Cell body fill — mirrors the on-screen tint for whoever holds that day
        if (fillRole === 'plaintiff') { pdf.setFillColor(219, 234, 254); pdf.rect(cx, y, colW, rowH, 'F'); }
        else if (fillRole === 'defendant') { pdf.setFillColor(252, 231, 243); pdf.rect(cx, y, colW, rowH, 'F'); }
        else if (fillRole === 'exchange') { pdf.setFillColor(254, 243, 199); pdf.rect(cx, y, colW, rowH, 'F'); }
        pdf.setFont('times', 'normal'); pdf.setFontSize(6); pdf.setTextColor(0, 0, 0);
        var cellLabel = fillRole === 'plaintiff' ? 'Plaintiff' : fillRole === 'defendant' ? 'Defendant' : fillRole === 'exchange' ? 'Exchange' : '';
        if (cellLabel) pdf.text(cellLabel, cx + colW / 2, y + 5.5, { align: 'center' });
      });
      // Row borders
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
      pdf.rect(tableX, y, contentW, rowH);
      pdf.line(tableX + 16, y, tableX + 16, y + rowH);
      for (var di = 1; di < 7; di++) {
        pdf.line(tableX + 16 + di * colW, y, tableX + 16 + di * colW, y + rowH);
      }
      y += rowH;
    });

    addSpace(3);
    // Legend
    setBodyFont(8, 'italic');
    var legendText = 'Blue cells denote Plaintiff parenting time. Pink cells denote Defendant parenting time. Yellow cells denote exchange days.';
    pdf.text(legendText, margin, y, { maxWidth: contentW });
    var legendLines = pdf.splitTextToSize(legendText, contentW);
    y += legendLines.length * 4;
    writeClause('The above schedule shall repeat every four (4) weeks unless modified by agreement of the parties or order of the Court. Blue cells reflect parenting time with the Plaintiff, pink cells reflect parenting time with the Defendant, and yellow cells reflect exchange days on which custody transfers between the parties.');
  }

  // Exchange details
  if (planData.exchangeLocation) writeClause('The parties shall conduct all parenting time exchanges at ' + planData.exchangeLocation + ', unless otherwise agreed in writing by both parties.');
  if (planData.exchangeTime) writeClause('On days when school is not in session or the child is absent, exchanges shall occur at ' + planData.exchangeTime + '.');
  if (planData.schoolOutExchangeTime) writeClause('During school breaks and holidays, exchanges shall occur at ' + planData.schoolOutExchangeTime + '.');
  if (planData.schoolCustodyTransition) writeClause('On school days, custody transitions shall occur in accordance with the following terms, namely ' + planData.schoolCustodyTransition + '.');

  // Supervised visitation location / facility clause
  if (planData.supervisedVisitation === 'yes') {
    var svFacility = (planData.supervisedVisitationFacilityName || '').trim();
    var svAddress = (planData.supervisedVisitationLocation || '').trim();
    if (svFacility && svAddress) {
      writeClause('All supervised visitation sessions shall occur at ' + svFacility + ', located at ' + svAddress + ', or at such other location as the parties may agree to in writing.');
    } else if (svFacility) {
      writeClause('All supervised visitation sessions shall occur at ' + svFacility + ', or at such other location as the parties may agree to in writing.');
    } else if (svAddress) {
      writeClause('All supervised visitation sessions shall occur at the following location, namely ' + svAddress + ', or at such other location as the parties may agree to in writing.');
    }
  }

  // Scheduled Events (supervised visitation sessions, therapy, special exchanges, etc.)
  var scheduledEventsList = (planData.scheduleEvents || []).filter(function (ev) {
    return ev && (String(ev.name || '').trim() || String(ev.dayOfWeek || '').trim() || String(ev.location || '').trim());
  });
  if (scheduledEventsList.length > 0) {
    addSpace(3);
    checkPage(30);
    writeLine('Scheduled Events', { size: 10, style: 'bold' });
    addSpace(2);
    var EVENT_TYPE_LABELS = {
      'supervised-visitation': 'Supervised Visitation',
      'therapy': 'Therapy / Counseling',
      'medical': 'Medical Appointment',
      'school': 'School Event',
      'extracurricular': 'Extracurricular Activity',
      'exchange': 'Special Exchange',
      'other': 'Other',
    };
    var FREQUENCY_LABELS = {
      'weekly': 'Weekly',
      'biweekly': 'Every Other Week',
      'monthly': 'Monthly',
      'first-of-month': 'First of the Month',
      'last-of-month': 'Last of the Month',
      'as-needed': 'As Needed',
      'one-time': 'One-Time',
    };
    // Table columns: Type | Day of Week | Frequency | Time | Duration | Location | Notes
    var evColW = [contentW * 0.15, contentW * 0.12, contentW * 0.13, contentW * 0.09, contentW * 0.1, contentW * 0.25, contentW * 0.16];
    var evHeaderH = 7;
    var evX = margin;

    // Header row
    checkPage(evHeaderH + 20);
    pdf.setFillColor(230, 230, 230);
    pdf.rect(evX, y, contentW, evHeaderH, 'F');
    pdf.setFont('times', 'bold'); pdf.setFontSize(8); pdf.setTextColor(0, 0, 0);
    var evHeaders = ['Type', 'Day', 'Frequency', 'Time', 'Duration', 'Location / Facility', 'Notes'];
    var evCx = evX;
    evHeaders.forEach(function (h, i) {
      pdf.text(h, evCx + 2, y + 4.5);
      evCx += evColW[i];
    });
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
    pdf.rect(evX, y, contentW, evHeaderH);
    var evBorderX = evX;
    for (var ebi = 0; ebi < evColW.length - 1; ebi++) {
      evBorderX += evColW[ebi];
      pdf.line(evBorderX, y, evBorderX, y + evHeaderH);
    }
    y += evHeaderH;

    // Data rows
    scheduledEventsList.forEach(function (ev) {
      pdf.setFont('times', 'normal'); pdf.setFontSize(8);
      var rowCells = [
        EVENT_TYPE_LABELS[ev.type] || 'Event',
        String(ev.dayOfWeek || '').trim() || '\u2014',
        FREQUENCY_LABELS[ev.frequency] || (String(ev.frequency || '').trim() || '\u2014'),
        String(ev.time || '').trim() || '\u2014',
        String(ev.duration || '').trim() || '\u2014',
        (String(ev.name || '').trim() ? String(ev.name).trim() + ' \u2014 ' : '') + (String(ev.location || '').trim() || '\u2014'),
        String(ev.notes || '').trim() || '\u2014',
      ];
      // Compute wrapped lines per column to determine row height
      var wrappedLines = rowCells.map(function (c, i) {
        return pdf.splitTextToSize(String(c), evColW[i] - 4);
      });
      var maxLines = wrappedLines.reduce(function (m, a) { return Math.max(m, a.length); }, 1);
      var evRowH = Math.max(7, maxLines * 4 + 2);
      checkPage(evRowH + 2);

      var rCx = evX;
      wrappedLines.forEach(function (lines, i) {
        var ty = y + 4.5;
        lines.forEach(function (ln) {
          pdf.text(ln, rCx + 2, ty);
          ty += 4;
        });
        rCx += evColW[i];
      });
      // Row border
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
      pdf.rect(evX, y, contentW, evRowH);
      var vbx = evX;
      for (var ei = 0; ei < evColW.length - 1; ei++) {
        vbx += evColW[ei];
        pdf.line(vbx, y, vbx, y + evRowH);
      }
      y += evRowH;
    });

    addSpace(3);
    writeClause('The scheduled events set forth in the table above are incorporated into this Parenting Plan as specific commitments involving ' + childName + '. Each parent shall facilitate the child\'s attendance at these events and shall not unreasonably interfere with any such scheduled event during their parenting time. Where attendance alternates between the parties, if a party declines their opportunity to attend, the rotation shall continue without adjustment.');
  }

  // Day-to-day decisions
  writeClause('Each party shall make routine day-to-day decisions affecting ' + childName + ' during that party\'s parenting time, subject to the major decision-making provisions of this Parenting Plan.');

  // ===== VIII. HOLIDAYS & VACATION =====
  addSection('HOLIDAYS AND VACATION');

  // Hierarchy of Parenting Time — Mandatory precedence clause
  addSpace(2);
  pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
  checkPage(50);
  pdf.text('Hierarchy of Parenting Time', margin, y);
  y += 5;
  pdf.setFont('times', 'normal');
  
  writeClause('In the event of a conflict between parenting time provisions contained in this Parenting Plan, the following order of precedence shall apply:');
  addSpace(2);
  
  // Bullet points for hierarchy
  var hierarchyItems = [
    'Holiday Parenting Schedule shall take precedence over all other parenting time.',
    'Vacation Parenting Time shall take precedence over the Regular Parenting Schedule but shall not supersede the Holiday Parenting Schedule unless expressly stated herein or agreed to by the parties in writing.',
    'Regular Parenting Schedule shall apply at all times not otherwise modified by the Holiday Parenting Schedule or properly noticed Vacation Parenting Time or by modification of the parents after expressly agreeing in writing.'
  ];
  
  hierarchyItems.forEach(function(item, idx) {
    checkPage(12);
    var bulletX = margin + 8;
    var bulletTextX = margin + 13;
    var maxW = contentW - 13;
    pdf.setFont('times', 'normal'); pdf.setFontSize(10);
    pdf.text(String(idx + 1) + '.', bulletX, y);
    var lines = pdf.splitTextToSize(item, maxW);
    lines.forEach(function(line, li) {
      checkPage(6);
      pdf.text(line, li === 0 ? bulletTextX : bulletTextX, y);
      y += 4.5;
    });
    y += 1;
  });
  
  writeClause('In the event that the parties agree in writing to modify the regular parenting access time contained herein, the newly agreed to time shall not be revoked, interrupted, or interfered with in the same manner that non-modified regular access time is enforceable.');
  
  addSpace(4);
  pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3);
  pdf.line(margin, y, pw - margin, y);
  y += 6;

  // Holiday table
  var holidayNames = {
    thanksgiving: 'Thanksgiving', christmas_eve: 'Christmas Eve', christmas_day: 'Christmas Day',
    new_years: "New Year's Day", easter: 'Easter/Spring Holiday', st_patricks_day: "St. Patrick's Day", july4th: 'Independence Day (July 4th)',
    labor_day: 'Labor Day', memorial_day: 'Memorial Day', spring_break: 'Spring Break', fall_break: 'Fall Break',
  };
  var HOLIDAY_LABEL_MAP = {
    'plaintiff-odd_defendant-even': 'Plaintiff Odd Years / Defendant Even Years',
    'plaintiff-even_defendant-odd': 'Plaintiff Even Years / Defendant Odd Years',
    'always-plaintiff': 'Always with Plaintiff',
    'always-defendant': 'Always with Defendant',
  };

  writeClause('Holiday parenting time shall supersede the regular parenting schedule, and the holiday assignments set forth in the schedule below shall govern the parties\' observance of each holiday listed.');
  addSpace(2);

  // Holiday table
  checkPage(70);
  var holColW1 = contentW * 0.4;
  var holColW2 = contentW * 0.6;
  var holRowH = 7;
  var holX = margin;

  // Header
  pdf.setFillColor(230, 230, 230);
  pdf.rect(holX, y, holColW1, holRowH, 'F');
  pdf.rect(holX + holColW1, y, holColW2, holRowH, 'F');
  pdf.setFont('times', 'bold'); pdf.setFontSize(8); pdf.setTextColor(0, 0, 0);
  pdf.text('Holiday', holX + 3, y + 4.5);
  pdf.text('Assignment', holX + holColW1 + 3, y + 4.5);
  pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
  pdf.rect(holX, y, contentW, holRowH);
  pdf.line(holX + holColW1, y, holX + holColW1, y + holRowH);
  y += holRowH;

  // Rows
  var holEntries = Object.keys(holidayNames).map(function(k) {
    var val = planData.holidays[k];
    if (val && typeof val === 'string') return { name: holidayNames[k], assignment: HOLIDAY_LABEL_MAP[val] || val };
    return null;
  }).filter(Boolean);
  holEntries.push({ name: "Mother's Day", assignment: 'Always with Mother' });
  holEntries.push({ name: "Father's Day", assignment: 'Always with Father' });
  if (planData.customHolidays && planData.customHolidays.length > 0) {
    planData.customHolidays.forEach(function(ch) {
      if (ch.name) holEntries.push({ name: ch.name, assignment: HOLIDAY_LABEL_MAP[ch.assignment] || ch.assignment || 'As agreed' });
    });
  }

  holEntries.forEach(function(entry) {
    checkPage(holRowH + 2);
    pdf.setFont('times', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(0, 0, 0);
    pdf.text(entry.name, holX + 3, y + 4.5);
    pdf.text(entry.assignment, holX + holColW1 + 3, y + 4.5);
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
    pdf.rect(holX, y, contentW, holRowH);
    pdf.line(holX + holColW1, y, holX + holColW1, y + holRowH);
    y += holRowH;
  });

  addSpace(4);

  // Rule 3 + 5 + 10D — Forfeiture / continuation rule for holiday rotations.
  writeClause('If a party declines or is unable to exercise their scheduled holiday parenting time under the table above, the rotation shall continue without adjustment, and no make-up time shall be required absent the written agreement of the parties.');

  // Vacation
  if (planData.vacationWeeks) {
    var weeksMap = { '1': 'one (1) week', '2': 'two (2) weeks', '3': 'three (3) weeks', '4': 'four (4) weeks' };
    var weeksText = weeksMap[planData.vacationWeeks] || planData.vacationWeeks + ' week(s)';
    writeClause('Each parent shall be entitled to ' + weeksText + ' of uninterrupted vacation time per year with ' + childName + '. Vacation periods shall not conflict with the other parent\'s previously scheduled vacation time.');
  }
  if (planData.vacationNotice) {
    var vacNoticeMap = { '30days': 'thirty (30) days', '60days': 'sixty (60) days', '90days': 'ninety (90) days', '14days': 'fourteen (14) days' };
    var vacText = vacNoticeMap[planData.vacationNotice] || planData.vacationNotice.replace(/(\d+)days/, '$1 days');
    writeClause('The vacationing parent shall provide at least ' + vacText + ' advance written notice of intended vacation dates, including the destination, dates of travel, and contact information during the vacation period.');
  }
  if (planData.vacationDuringSchool === 'allowed') writeClause('Vacation during the regular school year shall be permitted, subject to applicable notice requirements and the provisions herein regarding missed school days.');
  if (planData.vacationDuringSchool === 'not-allowed') writeClause('Neither party shall schedule vacation with ' + childName + ' during the regular school year without express written consent of the other party.');
  if (planData.birthdayRule) {
    var bdayLabels = { 'with-custodial': childName + '\'s birthday shall be spent with the parent who has custody on that date under the regular parenting schedule.', 'alternating': 'The parties shall alternate ' + childName + '\'s birthday annually, with the Plaintiff having the child on even-numbered years and the Defendant on odd-numbered years, or as otherwise agreed.', 'split-day': childName + '\'s birthday shall be shared between the parties, with each parent having equal time with the child on that day.', 'party-each': 'Each parent shall be entitled to host a separate birthday celebration for ' + childName + ' during their respective parenting time.' };
    writeClause(bdayLabels[planData.birthdayRule] || childName + '\'s birthday shall be handled as follows: ' + planData.birthdayRule.replace(/-/g, ' ') + '.');
  }
  if (planData.schoolEventAttendance) {
    var seLabels = {
      'alternate': 'When attendance at a school event is limited to one parent, the parties shall alternate attendance. ' + (planData.schoolEventFirstAttendee === 'defendant' ? (dName + ' (Defendant)') : (pName + ' (Plaintiff)')) + ' shall attend the first school event of the academic year, and the parties shall alternate thereafter.',
      'plaintiff-primary': pName + ' (Plaintiff) shall be the primary attendee at school events where attendance is limited. ' + dName + ' (Defendant) shall attend when the Plaintiff is unable.',
      'defendant-primary': dName + ' (Defendant) shall be the primary attendee at school events where attendance is limited. ' + pName + ' (Plaintiff) shall attend when the Defendant is unable.',
      'custodial-parent': 'The parent exercising custody on the day of a school event with limited attendance shall attend that event.',
      'both-attend': 'Both parents shall be permitted to attend all school events, performances, and activities involving ' + childName + ', regardless of which parent has custody at the time.',
      'both-no-contact': 'Both parents may attend school events involving ' + childName + ', but shall maintain appropriate distance from one another and shall not engage in direct contact during the event.'
    };
    writeClause(seLabels[planData.schoolEventAttendance] || 'School event attendance shall be governed as agreed by the parties.');
  }

  // ===== IX. TRAVEL & RELOCATION =====
  addSection('TRAVEL AND RELOCATION');
  if (planData.travelNotice) {
    var tvNoticeMap = { '24h': 'twenty-four (24) hours', '48h': 'forty-eight (48) hours', '72h': 'seventy-two (72) hours', '7days': 'seven (7) days', '14days': 'fourteen (14) days', '30days': 'thirty (30) days' };
    var tvText = tvNoticeMap[planData.travelNotice] || planData.travelNotice.replace(/(\d+)h/, '$1 hours').replace(/(\d+)days/, '$1 days');
    writeClause('The traveling parent shall provide at least ' + tvText + ' advance written notice of any out-of-area travel with ' + childName + ', including the intended destination, dates of travel, and contact information during the trip.');
  }
  if (planData.travelItinerary === 'required') writeClause('A complete travel itinerary, including destinations, dates, and contact information, shall be provided to the non-traveling parent.');
  if (planData.neighboringStatesExempt === 'exempt') writeClause('Travel to immediately neighboring states shall be exempt from the notice and itinerary requirements.');
  if (planData.internationalTravel) {
    var intlLabels = { 'both-consent': 'International travel shall require written consent of both parties.', 'plaintiff-consent': 'International travel by the Defendant shall require Plaintiff\'s written consent.', 'defendant-consent': 'International travel by the Plaintiff shall require Defendant\'s written consent.' };
    writeClause(intlLabels[planData.internationalTravel] || planData.internationalTravel);
  }
  if (planData.passportHolding) {
    var passLabels = { joint: childName + '\'s passport shall be held jointly, with possession alternating as needed for travel.', plaintiff: childName + '\'s passport shall be held by ' + pName + ' (Plaintiff).', defendant: childName + '\'s passport shall be held by ' + dName + ' (Defendant).' };
    writeClause(passLabels[planData.passportHolding] || planData.passportHolding);
  }
  if (planData.relocationNotice) {
    var relNoticeMap = { '30days': 'thirty (30) days', '60days': 'sixty (60) days', '90days': 'ninety (90) days', '120days': 'one hundred twenty (120) days' };
    var relText = relNoticeMap[planData.relocationNotice] || planData.relocationNotice.replace(/(\d+)days/, '$1 days');
    // Rule 10E — condition triggering notice (school district change or distance threshold).
    writeClause('Either party intending to relocate with the minor child, or to change the residence of the minor child in a manner that would result in a change of school district, a change of county of residence, or a material increase in travel distance for parenting time, shall provide at least ' + relText + ' advance written notice to the other party. Such notice shall include the proposed new address, the reason for the relocation, the proposed effective date of the move, and a proposed revised parenting time schedule addressing the impact of the relocation on the welfare of the minor child.');
  }
  if (planData.relocationDistance) {
    var relDistLabels = { 'out-of-state': 'Neither party shall relocate with ' + childName + ' outside of the state without prior written consent of the other party or order of the court.', '50-miles': 'Neither party shall relocate with ' + childName + ' more than fifty (50) miles from the current residence without prior written consent of the other party or order of the court.', '100-miles': 'Neither party shall relocate with ' + childName + ' more than one hundred (100) miles from the current residence without prior written consent of the other party or order of the court.', 'same-county': 'Neither party shall relocate with ' + childName + ' outside of the current county of residence without prior written consent of the other party or order of the court.' };
    writeClause(relDistLabels[planData.relocationDistance] || 'Relocation with ' + childName + ' shall be restricted as follows: ' + planData.relocationDistance.replace(/-/g, ' ') + '.');
  }
  if (planData.exchangeTravelCost) {
    var travelCostLabels = {
      'equal-split': 'All transportation costs incurred in connection with custody exchanges, including but not limited to fuel, tolls, parking, and airfare, shall be shared by the parties, with each party responsible for fifty percent (50%) of the total transportation costs. The party who incurs the cost shall provide documentation to the other party, and the other party shall reimburse their fifty percent (50%) share within a reasonable time of receipt of such documentation.',
      'sending-parent': 'The parent transporting ' + childName + ' to the custody exchange location (the sending parent) shall bear one hundred percent (100%) of all transportation costs associated with that exchange, including but not limited to fuel, tolls, parking, and airfare.',
      'receiving-parent': 'The parent picking up ' + childName + ' from the custody exchange location (the receiving parent) shall bear one hundred percent (100%) of all transportation costs associated with that exchange, including but not limited to fuel, tolls, parking, and airfare.',
      'plaintiff-pays': pName + ' (Plaintiff) shall bear one hundred percent (100%) of all transportation costs associated with custody exchanges, including but not limited to fuel, tolls, parking, and airfare.',
      'defendant-pays': dName + ' (Defendant) shall bear one hundred percent (100%) of all transportation costs associated with custody exchanges, including but not limited to fuel, tolls, parking, and airfare.',
      'proportional-income': 'Transportation costs incurred in connection with custody exchanges, including but not limited to fuel, tolls, parking, and airfare, shall be divided between the parties in proportion to their respective gross incomes, as determined at the time the expense is incurred. The party who incurs the cost shall provide documentation to the other party, and the other party shall reimburse their proportional share within a reasonable time of receipt of such documentation.',
      'own-costs': 'Each party shall be solely responsible for their own transportation costs incurred in connection with custody exchanges. Neither party shall seek reimbursement from the other party for such costs.',
    };
    writeClause(travelCostLabels[planData.exchangeTravelCost] || 'Transportation costs for custody exchanges shall be allocated as follows: ' + planData.exchangeTravelCost.replace(/-/g, ' ') + '.');
    if (planData.exchangeTravelMileageRate) {
      var mileageLabels = {
        'irs-rate': 'Mileage reimbursement, where applicable under the cost-sharing provisions set forth herein, shall be calculated at the then-current Internal Revenue Service standard mileage rate for the use of a personal vehicle.',
        'flat-rate': 'Mileage reimbursement, where applicable under the cost-sharing provisions set forth herein, shall be calculated at a fixed rate of fifty cents ($0.50) per mile.',
        'actual-cost': 'Reimbursement for transportation costs, where applicable under the cost-sharing provisions set forth herein, shall be based on actual costs incurred, supported by receipts, fuel records, or other reasonable documentation.',
      };
      writeClause(mileageLabels[planData.exchangeTravelMileageRate] || 'Mileage reimbursement shall be determined as follows: ' + planData.exchangeTravelMileageRate.replace(/-/g, ' ') + '.');
    }
  }

  // ===== X. FINANCIAL =====
  addSection('FINANCIAL PROVISIONS');
  // NOTE: Child support is intentionally rendered as its own section at the end of the plan
  // (see CHILD SUPPORT section below). See Rule 12.
  if (planData.medicalExpenses) {
    var medExpLabels = {
      '50-50': 'The parties shall share all medical, dental, and vision costs for ' + childName + ' that are not covered by insurance, with each party responsible for fifty percent (50%) of such costs. Insurance shall be applied first, and the remaining balance shall be divided as stated herein. The party who incurs or pays the cost shall provide documentation to the other party, and the other party shall reimburse their share within a reasonable time of receipt of such documentation.',
      'proportional': 'The parties shall share all medical, dental, and vision costs for ' + childName + ' that are not covered by insurance in proportion to their respective gross incomes. Insurance shall be applied first, and the remaining balance shall be divided proportionally. The party who incurs or pays the cost shall provide documentation to the other party, and the other party shall reimburse their proportional share within a reasonable time of receipt of such documentation.',
      'plaintiff-pays': pName + ' (Plaintiff) shall be solely responsible for all medical, dental, and vision costs for ' + childName + ' that are not covered by insurance. Insurance shall be applied first, and ' + pName + ' (Plaintiff) shall bear one hundred percent (100%) of the remaining balance.',
      'defendant-pays': dName + ' (Defendant) shall be solely responsible for all medical, dental, and vision costs for ' + childName + ' that are not covered by insurance. Insurance shall be applied first, and ' + dName + ' (Defendant) shall bear one hundred percent (100%) of the remaining balance.',
    };
    writeClause(medExpLabels[planData.medicalExpenses] || 'Medical, dental, and vision costs for ' + childName + ' that are not covered by insurance shall be allocated as follows: ' + planData.medicalExpenses.replace(/-/g, ' ') + '.');
  }
  if (planData.educationExpenses) {
    var eduEqualText = 'Educational related expenses for the child shall be evenly split between the parties.';
    var eduExpLabels = {
      'equal': eduEqualText,
      '50-50': eduEqualText,
      'proportional': 'The parties shall share all agreed education-related expenses for ' + childName + ', including but not limited to tuition, fees, supplies, and required materials, in proportion to their respective gross incomes. The party who incurs or pays the cost shall provide documentation to the other party, and the other party shall reimburse their proportional share within a reasonable time of receipt of such documentation.',
      'discussed': 'Education-related expenses for ' + childName + ' shall be discussed by the parties on a per-expense basis. Neither party shall be obligated to contribute to such an expense unless both parties have agreed to the expense in advance and in writing.',
      'plaintiff': pName + ' (Plaintiff) shall be solely responsible for all agreed education-related expenses for ' + childName + ', including but not limited to tuition, fees, supplies, and required materials. ' + pName + ' (Plaintiff) shall bear one hundred percent (100%) of such costs.',
      'plaintiff-pays': pName + ' (Plaintiff) shall be solely responsible for all agreed education-related expenses for ' + childName + ', including but not limited to tuition, fees, supplies, and required materials. ' + pName + ' (Plaintiff) shall bear one hundred percent (100%) of such costs.',
      'defendant': dName + ' (Defendant) shall be solely responsible for all agreed education-related expenses for ' + childName + ', including but not limited to tuition, fees, supplies, and required materials. ' + dName + ' (Defendant) shall bear one hundred percent (100%) of such costs.',
      'defendant-pays': dName + ' (Defendant) shall be solely responsible for all agreed education-related expenses for ' + childName + ', including but not limited to tuition, fees, supplies, and required materials. ' + dName + ' (Defendant) shall bear one hundred percent (100%) of such costs.',
    };
    writeClause(eduExpLabels[planData.educationExpenses] || 'Education-related expenses for ' + childName + ' shall be allocated as follows: ' + planData.educationExpenses.replace(/-/g, ' ') + '.');
  }
  if (planData.extracurricularExpenses) {
    var extExpLabels = {
      '50-50': 'The parties shall jointly approve the minor child\'s participation in extracurricular activities. The parties shall share the costs of such agreed activities, with each party responsible for fifty percent (50%) of the associated costs. The party who incurs or pays the cost shall provide documentation to the other party, and the other party shall reimburse their share within a reasonable time of receipt of such documentation.',
      'proportional': 'The parties shall jointly approve the minor child\'s participation in extracurricular activities. The parties shall share the costs of such agreed activities in proportion to their respective gross incomes. The party who incurs or pays the cost shall provide documentation to the other party, and the other party shall reimburse their proportional share within a reasonable time of receipt of such documentation.',
      'enrolling-parent': 'The parent who enrolls ' + childName + ' in an extracurricular activity shall bear one hundred percent (100%) of the cost of that activity, unless both parties agree otherwise in writing prior to enrollment.',
      'plaintiff-pays': pName + ' (Plaintiff) shall be solely responsible for all agreed extracurricular activity costs for ' + childName + '. ' + pName + ' (Plaintiff) shall bear one hundred percent (100%) of such costs.',
      'defendant-pays': dName + ' (Defendant) shall be solely responsible for all agreed extracurricular activity costs for ' + childName + '. ' + dName + ' (Defendant) shall bear one hundred percent (100%) of such costs.',
    };
    writeClause(extExpLabels[planData.extracurricularExpenses] || 'Extracurricular activity costs for ' + childName + ' shall be allocated as follows: ' + planData.extracurricularExpenses.replace(/-/g, ' ') + '.');
  }
  if (planData.clothingPolicy) {
    var clothLabels = {
      'each-provides': 'Each party shall maintain an adequate wardrobe for ' + childName + ' at their respective residence, at their own sole cost and expense. Clothing shall travel with ' + childName + ' between residences and shall be returned promptly.',
      'shared': 'The parties shall share the cost of clothing for ' + childName + ', with each party responsible for fifty percent (50%) of clothing expenses. Clothing shall travel freely with ' + childName + ' between residences. The party who purchases clothing shall provide receipts to the other party, and the other party shall reimburse their share within a reasonable time.',
      'plaintiff-provides': pName + ' (Plaintiff) shall be the primary provider of clothing for ' + childName + ' and shall bear one hundred percent (100%) of such costs. Clothing shall travel with ' + childName + ' between residences.',
      'defendant-provides': dName + ' (Defendant) shall be the primary provider of clothing for ' + childName + ' and shall bear one hundred percent (100%) of such costs. Clothing shall travel with ' + childName + ' between residences.',
    };
    writeClause(clothLabels[planData.clothingPolicy] || 'Clothing for ' + childName + ' shall be handled as follows: ' + planData.clothingPolicy.replace(/-/g, ' ') + '.');
  }
  if (planData.taxDependency) {
    var altText = 'The parties shall alternate the right to claim ' + childName + ' as a tax dependent on an annual basis, as set forth in the schedule below. The party not claiming the exemption in a given year shall execute IRS Form 8332 or its equivalent in a timely manner upon request.';
    var perIrsText = 'The right to claim ' + childName + ' as a tax dependent each tax year shall be determined in accordance with applicable Internal Revenue Service rules and the residency-based tiebreakers set forth in 26 U.S.C. \u00A7 152.';
    var taxLabels = {
      'alternate': altText,
      'alternating': altText,
      'plaintiff': pName + ' (Plaintiff) shall claim ' + childName + ' as a tax dependent each year. ' + dName + ' (Defendant) shall execute IRS Form 8332 or its equivalent in a timely manner upon request.',
      'defendant': dName + ' (Defendant) shall claim ' + childName + ' as a tax dependent each year. ' + pName + ' (Plaintiff) shall execute IRS Form 8332 or its equivalent in a timely manner upon request.',
      'per-irs': perIrsText,
      'per-agreement': 'The right to claim ' + childName + ' as a tax dependent shall be allocated as the parties agree in writing each year. In the absence of agreement, the right shall be determined in accordance with applicable federal tax law.',
    };
    writeClause(taxLabels[planData.taxDependency] || 'The tax dependency exemption for ' + childName + ' shall be allocated as follows: ' + planData.taxDependency.replace(/-/g, ' ') + '.');

    // Rule 11 — Table Enhancement: visually reinforce the alternating rotation.
    if (planData.taxDependency === 'alternate' || planData.taxDependency === 'alternating') {
      addSpace(2);
      checkPage(30);
      var txColW1 = contentW * 0.35;
      var txColW2 = contentW * 0.65;
      var txRowH = 7;
      var txX = margin;

      pdf.setFillColor(230, 230, 230);
      pdf.rect(txX, y, txColW1, txRowH, 'F');
      pdf.rect(txX + txColW1, y, txColW2, txRowH, 'F');
      pdf.setFont('times', 'bold'); pdf.setFontSize(8); pdf.setTextColor(0, 0, 0);
      pdf.text('Tax Year', txX + txColW1 / 2, y + 4.5, { align: 'center' });
      pdf.text('Claiming Party', txX + txColW1 + txColW2 / 2, y + 4.5, { align: 'center' });
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
      pdf.rect(txX, y, contentW, txRowH);
      pdf.line(txX + txColW1, y, txX + txColW1, y + txRowH);
      y += txRowH;

      var evenParty = (planData.taxEvenYears === 'defendant') ? dName + ' (Defendant)' : pName + ' (Plaintiff)';
      var oddParty = (planData.taxOddYears === 'plaintiff') ? pName + ' (Plaintiff)' : dName + ' (Defendant)';
      var txRows = [
        { year: 'Even-Numbered Years', party: evenParty },
        { year: 'Odd-Numbered Years', party: oddParty },
      ];
      txRows.forEach(function (r) {
        checkPage(txRowH + 2);
        pdf.setFont('times', 'normal'); pdf.setFontSize(8); pdf.setTextColor(0, 0, 0);
        pdf.text(r.year, txX + txColW1 / 2, y + 4.5, { align: 'center' });
        pdf.text(r.party, txX + txColW1 + txColW2 / 2, y + 4.5, { align: 'center' });
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.5);
        pdf.rect(txX, y, contentW, txRowH);
        pdf.line(txX + txColW1, y, txX + txColW1, y + txRowH);
        y += txRowH;
      });
      addSpace(3);
    }
  }

  // ===== XI. CONDUCT BOUNDARIES =====
  addSection('CONDUCT BOUNDARIES');
  if (planData.introNewPartner) {
    var threeMoText = 'The parties shall not introduce the child to new romantic partners unless a period of no less than three months of meaningful and exclusive dating has passed.';
    var sixMoText = 'The parties shall not introduce the child to new romantic partners unless a period of no less than six months of meaningful and exclusive dating has passed.';
    var npLabels = {
      '3months': threeMoText,
      '3-months': threeMoText,
      '6months': sixMoText,
      '6-months': sixMoText,
      'engaged': 'The parties shall not introduce the child to new romantic partners until the introducing party is engaged to be married to that individual or has entered into a similarly committed long-term relationship.',
      'notify': 'Neither party shall introduce the child to a new romantic partner without first providing reasonable advance written notice to the other party regarding the identity of the partner and the intended timing of the introduction.',
      'no-restriction': 'There shall be no time-based restriction on the introduction of new romantic partners to ' + childName + ', provided that all such introductions are conducted in a manner that is age-appropriate and sensitive to the child\'s emotional well-being.',
      'mutual-agreement': 'Neither party shall introduce ' + childName + ' to a new romantic partner without first providing written notice to the other party and obtaining mutual agreement regarding the timing and manner of introduction. The parties shall act in good faith in reaching such agreement.',
    };
    writeClause(npLabels[planData.introNewPartner] || 'The introduction of new romantic partners to ' + childName + ' shall be governed as follows: ' + planData.introNewPartner.replace(/-/g, ' ') + '.');
  }
  if (planData.newPartnerOvernights) {
    var engagedText = 'The child shall not be present for overnight dates or stays with a romantic partner until the parties are engaged to be married.';
    var overnightLabels = {
      'engaged-only': engagedText,
      'after-engagement': engagedText,
      'not-until-intro': 'No romantic partner shall stay overnight in the residence while ' + childName + ' is present until that partner has been properly introduced to the child in accordance with this Parenting Plan\'s introduction provisions.',
      '6months-dating': 'No romantic partner shall stay overnight in the residence while ' + childName + ' is present until the introducing party has been in a continuous and exclusive romantic relationship with that partner for a period of at least six (6) consecutive months.',
      'never': 'No romantic partner who is not married to the party shall stay overnight in the residence while ' + childName + ' is present in the home, regardless of the duration of the relationship.',
      'no-overnights': 'No romantic partner who is not married to or in a domestic partnership with the party shall stay overnight in the residence during that party\'s parenting time while ' + childName + ' is present in the home. This restriction shall remain in effect regardless of the duration of the romantic relationship.',
      'no-restriction': 'There shall be no specific time-based restriction on overnight stays by romantic partners during parenting time, provided that the environment in the home remains safe, stable, and appropriate for ' + childName + '\'s emotional and physical well-being.',
    };
    writeClause(overnightLabels[planData.newPartnerOvernights] || 'Overnight stays by romantic partners shall be governed as follows: ' + planData.newPartnerOvernights.replace(/-/g, ' ') + '.');
  }
  if (planData.socialMediaPolicy) {
    var smLabels = { 'no-posting': 'Neither party shall post photographs, videos, or identifying information about ' + childName + ' on social media without the express written consent of the other party.', 'limited': 'Each party may post photographs and updates regarding ' + childName + ' on social media, provided that no location data, school name, or identifying details are included.', 'no-restriction': 'There shall be no specific restriction on social media posts regarding ' + childName + ', provided such posts do not disparage the other party or place the child at risk.' };
    writeClause(smLabels[planData.socialMediaPolicy] || 'Social media posting regarding ' + childName + ' shall be governed as follows: ' + planData.socialMediaPolicy.replace(/-/g, ' ') + '.');
  }
  if (planData.disparagement === 'no-disparagement') {
    writeClause('Neither party shall make disparaging, derogatory, or demeaning remarks about the other party, the other party\'s family, or the other party\'s household members, in the presence of or within earshot of ' + childName + '. Neither party shall permit any third party to make such remarks in the child\'s presence.');
  }
  if (planData.substancePolicy) {
    var subLabels = { 'no-use': 'Neither party shall use illegal substances or abuse prescription medication at any time while exercising parenting time with ' + childName + '.', 'no-impairment': 'Neither party shall be under the influence of any substance that impairs their ability to care for ' + childName + ' during their parenting time.', 'testing': 'Either party may request substance testing of the other party if there is a good faith belief that substance use is affecting the child\'s safety during parenting time.' };
    writeClause(subLabels[planData.substancePolicy] || 'The following substance use policy shall apply during parenting time: ' + planData.substancePolicy.replace(/-/g, ' ') + '.');
  }
  if (planData.alcoholPolicy) {
    var noIntoxText = 'The parties shall not be intoxicated by way of alcohol or under the influence of any unprescribed controlled substances or other illicit drugs while the child is in the custody and care of either parent.';
    var alcLabels = {
      'no-alcohol': 'Neither party shall consume alcohol during their parenting time with ' + childName + ' or within twelve (12) hours before exercising parenting time.',
      'no-intoxication': noIntoxText,
      'no-excess': noIntoxText,
      'no-restriction': 'There shall be no specific restriction on responsible alcohol consumption during parenting time, provided neither party becomes impaired while caring for ' + childName + '.',
    };
    writeClause(alcLabels[planData.alcoholPolicy] || 'The following alcohol policy shall apply during parenting time: ' + planData.alcoholPolicy.replace(/-/g, ' ') + '.');
  }

  // ===== XII. DISPUTE RESOLUTION =====
  addSection('DISPUTE RESOLUTION');
  if (planData.disputeProcess) {
    var drLabels = { mediation: 'Any dispute arising under this Parenting Plan shall first be submitted to mediation before either party may seek court intervention. The parties shall participate in mediation in good faith and shall share the cost of mediation as set forth herein.', 'direct-negotiation': 'The parties shall first attempt to resolve any dispute arising under this Parenting Plan through direct negotiation in good faith. If the parties are unable to resolve the dispute within a reasonable time, either party may seek mediation or court intervention.', 'mediation-first': 'Any dispute arising under this Parenting Plan shall first be submitted to mediation before either party may seek court intervention. The parties shall participate in mediation in good faith and shall share the cost of mediation as set forth herein.', 'collaborative': 'Any dispute arising under this Parenting Plan shall be resolved through a collaborative process using qualified collaborative professionals. Both parties shall engage in the collaborative process in good faith before seeking court intervention.', 'parenting-coordinator': 'The parties shall appoint a mutually agreed-upon parenting coordinator to assist in resolving ongoing disputes arising under this Parenting Plan. The parenting coordinator shall have authority to make recommendations and, where authorized by the court, binding decisions on day-to-day parenting disputes.', court: 'Any dispute arising under this Parenting Plan that cannot be resolved by agreement of the parties shall be submitted to the court for resolution.' };
    writeClause(drLabels[planData.disputeProcess] || 'Disputes arising under this Parenting Plan shall be resolved as follows: ' + planData.disputeProcess.replace(/-/g, ' ') + '.');
  }
  if (planData.mediationSessions) writeClause('The parties shall participate in a minimum of ' + planData.mediationSessions + ' mediation session(s) before either party may file a motion with the court regarding any non-emergency dispute under this Parenting Plan.');
  if (planData.mediationCost) {
    var equalCostText = 'The parties shall evenly split the cost to attend any amount of mediation sessions.';
    var medCostLabels = {
      'equal': equalCostText,
      '50-50': equalCostText,
      'proportional': 'The cost of mediation shall be shared by the parties in proportion to their respective gross incomes, as determined at the time mediation is initiated.',
      'plaintiff': pName + ' (Plaintiff) shall bear one hundred percent (100%) of the cost of mediation.',
      'plaintiff-pays': pName + ' (Plaintiff) shall bear one hundred percent (100%) of the cost of mediation.',
      'defendant': dName + ' (Defendant) shall bear one hundred percent (100%) of the cost of mediation.',
      'defendant-pays': dName + ' (Defendant) shall bear one hundred percent (100%) of the cost of mediation.',
    };
    writeClause(medCostLabels[planData.mediationCost] || 'The cost of mediation shall be allocated as follows: ' + planData.mediationCost.replace(/-/g, ' ') + '.');
  }
  if (planData.modificationProcess) {
    var modLabels = { 'written-agreement': 'This Parenting Plan may be modified at any time by written agreement of both parties, signed and dated by both parties.', 'mediation-first': 'Any proposed modification to this Parenting Plan shall first be discussed in mediation before either party may seek court intervention.', 'court-only': 'This Parenting Plan shall not be modified except by order of the court upon motion by either party.' };
    writeClause(modLabels[planData.modificationProcess] || 'Modifications to this Parenting Plan shall be governed as follows: ' + planData.modificationProcess.replace(/-/g, ' ') + '.');
  }
  if (planData.reviewMilestones === 'yes') {
    writeClause('The parties shall review and reassess the terms of this Parenting Plan upon the occurrence of the following milestones, namely elementary school completion, middle school completion, completion of second year of high school, IEP-placed, significant-injury, and ADA-diagnosis. Such review shall be conducted in good faith and may result in modification by written agreement or order of the Court.');
  }

  // ===== XII. CHILD SUPPORT (Rule 12 — final substantive section of the Parenting Plan) =====
  addSection('CHILD SUPPORT');
  var csLabels = {
    'plaintiff-pays': pName + ' (Plaintiff) shall pay child support to ' + dName + ' (Defendant) for the support and maintenance of the minor child in an amount calculated in accordance with the applicable state child support guidelines. Payment shall be made on a schedule determined by the Court or as agreed by the parties in writing, and shall continue for so long as the applicable guidelines and law require.',
    'defendant-pays': dName + ' (Defendant) shall pay child support to ' + pName + ' (Plaintiff) for the support and maintenance of the minor child in an amount calculated in accordance with the applicable state child support guidelines. Payment shall be made on a schedule determined by the Court or as agreed by the parties in writing, and shall continue for so long as the applicable guidelines and law require.',
    'per-guidelines': 'Child support shall be calculated and paid in accordance with the applicable state child support guidelines. The amount and schedule of payment shall conform to the guidelines in effect at the time of the order.',
    'waived': 'The parties have knowingly and voluntarily agreed to waive the payment of child support at this time. Either party retains the right to seek the establishment or modification of child support through the Court at any time based upon a material change in circumstances affecting the minor child.',
    'offset': 'Child support between the parties shall be determined by applying a child support offset calculation based upon the parenting time allocation set forth herein, in accordance with the applicable state child support guidelines. The resulting obligation, if any, shall be paid on a schedule determined by the Court or as agreed by the parties in writing.'
  };
  if (planData.childSupport && csLabels[planData.childSupport]) {
    writeClause(csLabels[planData.childSupport]);
  } else {
    // Rule 12 — standard clause used when the user has not selected a specific arrangement.
    writeClause('Child support shall be calculated and paid in accordance with the applicable state child support guidelines. The amount and schedule of payment shall conform to the guidelines in effect at the time of the order.');
  }
  // Rule 13 — Child support supremacy clause when modifying an existing order.
  if (planData.existingOrder === 'yes') {
    writeClause('The terms of this Order regarding child support shall supersede all prior orders of this Court regarding the support of the minor child, and any provision of any prior order inconsistent with this Parenting Plan shall be deemed modified to the extent necessary to conform to this Order.');
  }

  // ===== XIII. CONTINUING JURISDICTION (Rule 14 — required closing clause) =====
  var courtLabel = (planData.courtName && planData.courtName.trim()) ? planData.courtName.trim() : 'this Court';
  writeClause(courtLabel + ' retains continuing and exclusive jurisdiction over the minor child common to the parties for all matters arising under or relating to this Parenting Plan, including enforcement and modification hereof.');

  // ===== SIGNATURE BLOCK =====
  addSpace(10);
  checkPage(40);
  drawHLine();
  addSpace(8);
  setBodyFont(10);

  // Plaintiff signature
  pdf.text('____________________________________', margin, y);
  pdf.text('______________', pw - margin - 40, y);
  y += 5;
  pdf.setFont('times', 'bold');
  pdf.text(pName + ' (Plaintiff)', margin, y);
  pdf.text('Date', pw - margin - 40, y);
  y += 12;

  // Defendant signature
  pdf.text('____________________________________', margin, y);
  pdf.text('______________', pw - margin - 40, y);
  y += 5;
  pdf.text(dName + ' (Defendant)', margin, y);
  pdf.text('Date', pw - margin - 40, y);
  y += 8;

  // ===== FOOTER ON ALL PAGES =====
  addPdfFooterToAllPages(pdf);
  pdf.save('parentum-parenting-plan.pdf');

  // Auto-save and grade after PDF download
  if (typeof onSaved === 'function') { onSaved(); }
}
